import { User, Advisor, CurriculumModule, Topic, UserRole, TopicStatus, AdvisorProgress, CurriculumLevel, EvaluationQuestion, EvaluationResult, AdvisorWithPassword, RolePlaySession, BrandingSettings, CertificateSettings, LibraryCategory, LibraryResource, ChatMessage, RolePlayFeedback, TrainingLesson, TrainingLessonInteraction } from '../types';

// La URL base de tu API. Para desarrollo local, sería 'http://localhost:3004'.
// Para producción, es '/api' y Nginx se encargará de redirigirlo al backend.
const API_BASE_URL = '/api';

// Función de ayuda para manejar las respuestas de la API de forma centralizada
const handleResponse = async (response: Response) => {
    if (response.status === 204) { // No Content
        return;
    }

    // Si la petición NO fue exitosa, proveer feedback específico.
    if (!response.ok) {
        if (response.status === 413) {
            throw new Error(`Error 413: Contenido Demasiado Grande. El archivo excede el límite del servidor. Contacta al administrador para aumentar el límite (client_max_body_size).`);
        }
        const rawBody = await response.text();
        let data: { error?: string } = {};
        if (rawBody) {
            try {
                data = JSON.parse(rawBody);
            } catch (e) {
                const safeSnippet = rawBody.slice(0, 180);
                throw new Error(`Error ${response.status}: ${response.statusText}. Respuesta no-JSON del servidor: ${safeSnippet}`);
            }
        }
        throw new Error(data.error || `Error ${response.status}: ${response.statusText}`);
    }

    // Si la petición fue exitosa, parsear el cuerpo JSON.
    try {
        const data = await response.json();
        return data;
    } catch (e) {
        throw new Error("Error: La respuesta del servidor no es un JSON válido.");
    }
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('apexUniversityToken') || localStorage.getItem('protegoToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const get = (endpoint: string) => fetch(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
}).then(handleResponse);
const post = (endpoint: string, body: any) => fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
}).then(handleResponse);
const put = (endpoint: string, body: any) => fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
}).then(handleResponse);
const del = (endpoint: string) => fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
}).then(handleResponse);


export const api = {
  // --- Auth ---
  login: (email: string, passwordIn: string): Promise<User | Advisor> => post('/login', { email, passwordIn }),

  // --- Curriculum ---
  getFullCurriculum: (): Promise<{ levels: CurriculumLevel[], modules: CurriculumModule[], topics: Topic[] }> => get('/curriculum/full'),
  addCurriculumLevel: (data: Omit<CurriculumLevel, 'id'>) => post('/levels', data),
  updateCurriculumLevel: (data: CurriculumLevel) => put(`/levels/${data.id}`, data),
  deleteCurriculumLevel: (id: string) => del(`/levels/${id}`),
  addCurriculumModule: (data: Omit<CurriculumModule, 'id'>) => post('/modules', data),
  updateCurriculumModule: (data: CurriculumModule) => put(`/modules/${data.id}`, data),
  deleteCurriculumModule: (id: string) => del(`/modules/${id}`),
  addTopic: (data: Omit<Topic, 'id'>) => post('/topics', data),
  updateTopic: (data: Topic) => put(`/topics/${data.id}`, data),
  deleteTopic: (id: string) => del(`/topics/${id}`),

  // --- Advisors ---
  getAdvisors: (): Promise<Advisor[]> => get('/advisors'),
  getDeletedAdvisors: (): Promise<Advisor[]> => get('/advisors/deleted'),
  getAdvisorById: (id: string): Promise<Advisor> => get(`/advisors/${id}`),
  addAdvisor: (data: { name: string, email: string, password?: string, role?: UserRole | string, managerId?: string | number | null, assignedTopics: string[], assignedModuleIds?: string[] }): Promise<Advisor> => post('/advisors', data),
  updateAdvisor: (data: Advisor): Promise<Advisor> => put(`/advisors/${data.id}`, data),
  updateTopicStatus: (advisorId: string, topicId: string, status: TopicStatus): Promise<AdvisorProgress> => post(`/advisors/${advisorId}/progress/${topicId}`, { status }),
  reorderTopics: (moduleId: string | number, orderedTopicIds: Array<string | number>) => post('/topics/reorder', { moduleId, orderedTopicIds }),

  // --- Questions ---
  getAllQuestions: (): Promise<EvaluationQuestion[]> => get('/questions'),
  getQuestionsForModule: (moduleId: string): Promise<EvaluationQuestion[]> => get(`/modules/${moduleId}/questions`),
  addQuestion: (data: Omit<EvaluationQuestion, 'id'>): Promise<EvaluationQuestion> => post('/questions', data),
  updateQuestion: (data: EvaluationQuestion): Promise<EvaluationQuestion> => put(`/questions/${data.id}`, data),
  deleteQuestion: (id: string) => del(`/questions/${id}`),

  // --- Evaluations ---
  saveEvaluationResult: (advisorId: string, evaluationId: string, result: EvaluationResult): Promise<AdvisorProgress> => post(`/advisors/${advisorId}/evaluations/${evaluationId}`, result),

  // --- Role Play ---
  getAllRolePlaySessions: (): Promise<RolePlaySession[]> => get('/roleplaysessions'),
  getRolePlaySessions: (advisorId: string): Promise<RolePlaySession[]> => get(`/advisors/${advisorId}/roleplaysessions`),
  saveRolePlaySession: (session: Omit<RolePlaySession, 'id'>): Promise<RolePlaySession> => post('/roleplaysessions', session),
  generateRolePlayResponse: (data: { advisorName: string, scenario: string, messages: ChatMessage[] }): Promise<{ text: string }> => post('/roleplay/generate-response', data),
  generateRolePlayFeedback: (data: { prompt: string }): Promise<RolePlayFeedback> => post('/roleplay/generate-feedback', data),

  // --- Settings ---
  getBrandingSettings: (): Promise<BrandingSettings | undefined> => get('/settings/branding').catch(() => undefined),
  saveBrandingSettings: (settings: Omit<BrandingSettings, 'key'>): Promise<BrandingSettings> => post('/settings/branding', settings),
  getCertificateSettings: (): Promise<CertificateSettings | undefined> => get('/settings/certificate').catch(() => undefined),
  saveCertificateSettings: (settings: Omit<CertificateSettings, 'key'>): Promise<CertificateSettings> => post('/settings/certificate', settings),

  // --- Library ---
  getLibraryCategories: (): Promise<LibraryCategory[]> => get('/library/categories'),
  addLibraryCategory: (data: { name: string }): Promise<LibraryCategory> => post('/library/categories', data),
  updateLibraryCategory: (category: LibraryCategory): Promise<LibraryCategory> => put(`/library/categories/${category.id}`, category),
  deleteLibraryCategory: (id: string) => del(`/library/categories/${id}`),
  getLibraryResources: (): Promise<LibraryResource[]> => get('/library/resources'),
  addLibraryResource: (data: Omit<LibraryResource, 'id'>): Promise<LibraryResource> => post('/library/resources', data),
  updateLibraryResource: (resource: LibraryResource): Promise<LibraryResource> => put(`/library/resources/${resource.id}`, resource),
  deleteLibraryResource: (id: string) => del(`/library/resources/${id}`),

  // --- Lesson Builder ---
  getTrainingLessons: (status?: string): Promise<TrainingLesson[]> => get(`/university/lesson-builder/lessons${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  createTrainingLesson: (data: Partial<TrainingLesson> & { google_slides_url?: string }): Promise<TrainingLesson> => post('/university/lesson-builder/lessons', data),
  getTrainingLesson: (id: string | number): Promise<TrainingLesson> => get(`/university/lesson-builder/lessons/${id}`),
  getTrainingLessonPlayer: (id: string | number): Promise<TrainingLesson> => get(`/university/lesson-builder/player/${id}`),
  updateTrainingLesson: (id: string | number, data: Partial<TrainingLesson>): Promise<TrainingLesson> => put(`/university/lesson-builder/lessons/${id}`, data),
  uploadTrainingLessonSource: (id: string | number, data: { fileData: string; fileName: string; fileType?: string }): Promise<TrainingLesson> => post(`/university/lesson-builder/lessons/${id}/upload`, data),
  convertTrainingLesson: (id: string | number): Promise<TrainingLesson> => post(`/university/lesson-builder/lessons/${id}/convert`, {}),
  getTrainingLessonSlides: (id: string | number) => get(`/university/lesson-builder/lessons/${id}/slides`),
  getTrainingLessonConversionStatus: (id: string | number) => get(`/university/lesson-builder/lessons/${id}/conversion-status`),
  createSlideInteraction: (slideId: string | number, data: Partial<TrainingLessonInteraction>): Promise<TrainingLessonInteraction> => post(`/university/lesson-builder/slides/${slideId}/interactions`, data),
  updateSlideInteraction: (interactionId: string | number, data: Partial<TrainingLessonInteraction>): Promise<TrainingLessonInteraction> => put(`/university/lesson-builder/interactions/${interactionId}`, data),
  deleteSlideInteraction: (interactionId: string | number) => del(`/university/lesson-builder/interactions/${interactionId}`),
  deleteTrainingLesson: (id: string | number, force = false) => del(`/university/lesson-builder/lessons/${id}${force ? '?force=true' : ''}`),
  publishTrainingLesson: (id: string | number): Promise<TrainingLesson> => post(`/university/lesson-builder/lessons/${id}/publish`, {}),
  archiveTrainingLesson: (id: string | number): Promise<TrainingLesson> => post(`/university/lesson-builder/lessons/${id}/archive`, {}),
};
